import React from 'react';
import { formatScore10, scanDateMs } from '../create/format';
import { EYE_LINE_PCT } from '../oracle/PortraitScoreBar';
import { COPY } from '../../contracts/copyMap';

// Instagram-style grid of historical scans for the Vault index (/vault).
// Each tile is a portrait thumbnail with the eye-line SCORE censor bar (brand
// signature) + capture date. Clicking a tile deep-links to /vault/:scan_id for
// the full breakdown. This is the visual counterpart to the detail page: a
// dense browsable grid here, the editorial single-scan layout there.

function tileDate(entry) {
  const ms = scanDateMs(entry);
  if (ms == null) return '';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const ScanTile = ({ entry, onClick }) => {
  const date = tileDate(entry);
  // The server MASKS a locked row (overall_score: null) but keeps the
  // thumbnail, so without this branch the bar renders a formatted null and the
  // tile reads as a scan whose score went missing — indistinguishable from a
  // bug. The bar is the right place to say so: it is where the number would
  // have been.
  const locked = entry.locked === true;
  const score = formatScore10(entry.overall_score);

  // Progress toward opening THIS scan. Absent on legacy payloads and on rows
  // from a server that predates the field, so every use is guarded — a missing
  // block degrades to the plain LOCKED badge rather than a bar reading 0 of 0.
  const done = Number(entry.unlock?.done);
  const required = Number(entry.unlock?.required);
  const hasProgress =
    locked && Number.isFinite(done) && Number.isFinite(required) && required > 0;
  const remaining = hasProgress ? Math.max(0, required - done) : 0;

  const lockedLabel = hasProgress
    ? `scan ${date} · ${COPY.VAULT_TILE_LOCKED_A11Y} · ${done} of ${required} ${COPY.VAULT_TILE_LOCKED_PROGRESS_A11Y}`
    : `scan ${date} · ${COPY.VAULT_TILE_LOCKED_A11Y}`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={locked ? lockedLabel : `scan ${date} · score ${score} out of 10`}
      className="group relative block w-full aspect-[4/5] rounded-card overflow-hidden border border-hairline-soft bg-vein cursor-pointer p-0 text-left transition-all duration-200 ease-mogged hover:-translate-y-0.5 hover:border-[rgba(201,164,92,0.5)] hover:shadow-[0_12px_30px_-10px_rgba(0,0,0,0.7)]"
    >
      {entry.thumbnail_url ? (
        <img
          src={entry.thumbnail_url}
          alt=""
          className={[
            'absolute inset-0 w-full h-full object-cover',
            // Not a censor — the photo is the user's own and they have already
            // seen it. The blur says "withheld reading", not "hidden face".
            locked ? 'blur-[3px] scale-105' : '',
          ].join(' ')}
        />
      ) : (
        <div className="absolute inset-0 bg-vein" role="img" aria-label="scan thumbnail placeholder" />
      )}

      {/* eye-line censor bar — anchored to the shared EYE_LINE_PCT so it
          tracks PortraitScoreBar instead of duplicating the magic number.
          SCORE when open, LABEL when sealed. */}
      <div
        className="absolute left-[8%] right-[8%] h-[26px] bg-obsidian flex items-baseline justify-center gap-1"
        style={{ top: `calc(${EYE_LINE_PCT}% - 13px)` }}
      >
        {locked ? (
          // Bone, never Aurum: a locked reading is a neutral status, not a
          // metric value, and Aurum is allow-listed to metrics and active
          // states (DESIGN.md 2.1). Never red — that rule is system-wide.
          <span className="font-censor font-bold uppercase text-bone-pure leading-[26px] text-[11px] tracking-[0.18em]">
            {COPY.VAULT_TILE_LOCKED}
          </span>
        ) : (
          <>
            <span className="font-display font-medium text-bone-pure leading-[26px] text-[15px] tabular-nums">
              {score}
            </span>
            <span className="text-[9px] text-[#888]">/10</span>
          </>
        )}
      </div>

      {/* date footer, plus the unlock meter on a locked tile.
          The meter is a segment per required judgment rather than a continuous
          bar: the thing being counted is discrete and small (ten), so pips let
          someone read "three down, seven to go" at a glance without doing
          percentage arithmetic — and they match the pip language the unlock
          gate already uses. */}
      <div className="absolute inset-x-0 bottom-0 px-3 pb-2.5 pt-8 bg-gradient-to-t from-black/80 to-transparent">
        <div className="font-display text-bone text-sm">{date}</div>

        {hasProgress && (
          <div className="mt-1.5" data-testid="vault-tile-unlock">
            <div className="flex gap-[3px]" aria-hidden="true">
              {Array.from({ length: required }, (unused, i) => (
                <div
                  key={i}
                  data-testid="vault-tile-pip"
                  data-filled={i < done ? 'true' : 'false'}
                  className={[
                    'h-[3px] flex-1 rounded-[2px]',
                    i < done ? 'bg-aurum' : 'bg-white/20',
                  ].join(' ')}
                />
              ))}
            </div>
            <div
              className="mt-1 font-display text-[10px] uppercase tracking-[0.16em] text-bone-dim tabular-nums"
              aria-hidden="true"
            >
              {`${remaining} ${COPY.VAULT_TILE_LOCKED_REMAINING}`}
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const VaultScanGrid = ({ entries, onSelect, onLoadMore, hasMore, loadingMore }) => (
  <div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4 mt-6">
      {entries.map((entry) => (
        <ScanTile key={entry.scan_id} entry={entry} onClick={() => onSelect(entry)} />
      ))}
    </div>
    {hasMore && (
      <div className="flex justify-center mt-9">
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="font-display uppercase tracking-[0.22em] text-xs text-bone-dim border border-hairline-soft rounded-field px-7 py-3 cursor-pointer transition-colors duration-200 hover:text-bone hover:border-[rgba(255,255,255,0.3)] disabled:opacity-50 disabled:cursor-default"
        >
          {loadingMore ? 'LOADING…' : 'LOAD MORE'}
        </button>
      </div>
    )}
  </div>
);

export default VaultScanGrid;
